"use client";

import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { disposeApi } from "@/api";
import { NotificationItem } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationItem";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { trackEvent } from "@/utils/trackEvent";
import { useLoadMore } from "@/utils/useLoadMore";

export const NotificationsPanel = observer(function NotificationsPanel({
  open,
  bottomInset = 0,
  hideHeader = false,
}: {
  open: boolean;
  /** Extra bottom padding inside the scroll area (px). Used by the mobile
   *  WebView to clear the home indicator once scrolled to the end; 0 on web. */
  bottomInset?: number;
  /** Hide the panel's title header. The mobile WebView renders its own native
   *  header, so it opts out of this one. */
  hideHeader?: boolean;
}) {
  const { notifications } = useStore();
  const t = useTranslations("sidebar.notifications");

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const setMoreSentinel = useLoadMore({
    root: scrollRoot,
    hasMore: open && notifications.hasMore,
    isLoading: notifications.isLoading,
    onLoadMore: (signal) => notifications.fetchMore(signal),
  });

  useEffect(
    function trackOpened() {
      if (!open) return;

      trackEvent("notifications_panel_opened", { unread_count: notifications.unreadCount });
    },
    [open, notifications],
  );

  useEffect(
    function initialFetch() {
      if (!open) return;

      return disposeApi(async (signal) => {
        await notifications.fetch(signal);
      });
    },
    [open, notifications],
  );

  const { list: items } = notifications;

  return (
    <div className="flex flex-col min-h-0">
      {!hideHeader && (
        <header className="px-[5px] py-2 pl-[14px] pt-[13px]">
          <h2 className="text-[12px] leading-[14px] font-mono tracking-tight uppercase">
            {t("title")}
          </h2>
        </header>
      )}
      <div
        ref={setScrollRoot}
        className="flex-1 overflow-y-auto px-[5px]"
        style={bottomInset ? { paddingBottom: bottomInset } : undefined}
      >
        {items.length === 0 && !notifications.hasMore && (
          <div className="flex flex-col items-center px-6.5 pt-5 pb-10 text-center">
            <span className="flex items-center justify-center">
              <Icon name="Notifications" width={34} height={34} />
            </span>
            <h3 className="font-semibold text-[17px] my-2 mb-3">{t("empty")}</h3>
            <p className="text-sm text-gray-500">{t("emptyDescription")}</p>
          </div>
        )}
        <ul className="flex flex-col">
          {items.map((notification) => (
            <li key={notification.id} data-notification-id={notification.id}>
              <NotificationItem notification={notification} />
            </li>
          ))}
        </ul>
        {notifications.hasMore && items.length > 0 && (
          <span ref={setMoreSentinel} aria-hidden className="block h-0" />
        )}
        {notifications.hasMore && notifications.isLoading && (
          <p className="px-4 py-3 text-center text-xs text-gray-500">{t("loadingMore")}</p>
        )}
      </div>
    </div>
  );
});
